import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Download, RefreshCw, Search } from 'lucide-react';

import axiosInstance from '../../../redux/api';
import { getStatusList, bulkUpdateOrderStatus } from '../../../redux/Order/action';
import { getAllHeaderCount } from '../../../redux/booking/action';
import { usePermissions } from '../../../Hooks/usePermissions';
import { useOrderList } from '../../../Hooks/useOrderList';
import { useDeferredLoading } from '../../../Hooks/useDeferredLoading';
import { MENU, PERMISSION } from '../../../lib/permissions';
import { useToasts, ToastStack } from '../../Common/Toast/useToasts';
import CommonPagination from '../../Common/Pagination/CommonPagination';

import OrderStatusTabs from './list/OrderStatusTabs';
import OrdersTable from './list/OrdersTable';
import BulkActionBar from './list/BulkActionBar';
import { buildOrderColumns } from './list/orderListColumns';
import ExportDateModal from './shared/ExportDateModal';
import ConfirmDialog from './detail/ConfirmDialog';
import TrackingPromptDialog, { trackingPromptCopy } from './detail/TrackingPromptDialog';
import { Button } from './detail/primitives';

import {
  buildOrderTabs,
  DEFAULT_ORDER_TAB,
  findOrderTab,
} from './shared/orderTabs';
import {
  isDestructiveStatus,
  isHoldStatus,
  isRetiredOption,
  isReturnStatus,
  sortByPath,
  statusPickerOptions,
} from './shared/orderStatus';
import { PER_PAGE_OPTIONS } from './shared/orderConstants';
import {
  COURIER_LABEL,
  SHIPMENT_DIRECTION,
  tripDirectionForStatus,
  tripNumberOnFile,
} from './shared/orderShipments';

/*
 * Orders list.
 *
 * The orchestrator: url state, data, and the mutations. Presentation lives in
 * ./list. Replaces OrderListPage + OrderListScreen, which split the same
 * concerns across two files and passed `onMutated` and `exportTrigger` between
 * them that the receiving component never destructured — which is why the
 * Refresh button re-fetched the tab counts and left the table untouched.
 *
 * Search, page and per-page live in the url alongside the tab, so a filtered
 * view survives a reload and can be sent to someone else.
 */

const BULK_MAX = 100;
const SEARCH_DEBOUNCE_MS = 400;

const readApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.response?.message ||
  fallback;

/** Debounced search input. Local draft so typing never re-runs the query. */
const SearchBar = ({ value, onChange }) => {
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(value);

  // Adopt external changes (Clear search, back button) without echoing them
  // back out as a new query.
  useEffect(() => {
    committedRef.current = value;
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === committedRef.current) return undefined;
    const timer = setTimeout(() => {
      committedRef.current = draft;
      onChange(draft);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, onChange]);

  return (
    <div className="relative flex-1 min-w-[240px]">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nysmah-muted pointer-events-none"
        aria-hidden="true"
      />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Search order #, customer, product or mobile…"
        aria-label="Search orders"
        className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#DFE6E9] bg-white text-[12.5px] text-nysmah-dark placeholder:text-nysmah-muted focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent"
      />
    </div>
  );
};

const OrderListPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tab: tabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAction, isSuperAdmin } = usePermissions();

  const statusList = useSelector((state) => state.reusableSlice.StatusList);
  const headerCount = useSelector(
    (state) => state?.bookingSlice?.getHeaderCount,
  );

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState(null);
  const [isBulkRunning, setBulkRunning] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [isExportOpen, setExportOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  // A row move that starts a trip (handed to shipping, returned) asks for
  // the trip's tracking number first: { order, statusId, direction }.
  const [trackingPrompt, setTrackingPrompt] = useState(null);
  const [isTrackingPromptPending, setTrackingPromptPending] = useState(false);

  useEffect(() => {
    dispatch(getStatusList());
    dispatch(getAllHeaderCount());
  }, [dispatch]);

  /* ---------- url state ---------- */

  const search = searchParams.get('q') || '';
  const page = Number(searchParams.get('page')) || 1;
  const perPage = Number(searchParams.get('per_page')) || 10;

  const patchUrl = useCallback(
    (patch) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(patch).forEach(([key, value]) => {
            if (value === '' || value == null) next.delete(key);
            else next.set(key, String(value));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // `retired` rides along so a status that still names old orders (Rejected)
  // keeps its label everywhere without ever being offered as a destination.
  const statusOptions = useMemo(
    () =>
      Array.isArray(statusList)
        ? statusList.map((item) => ({
            value: item?.id,
            label: item?.title,
            retired: Boolean(item?.retired),
          }))
        : [],
    [statusList],
  );

  const statusLabelById = useMemo(() => {
    const map = new Map();
    statusOptions.forEach((option) => map.set(option.value, option.label));
    return map;
  }, [statusOptions]);

  const tabs = useMemo(() => buildOrderTabs(statusOptions), [statusOptions]);
  const activeTab = useMemo(
    () => findOrderTab(tabs, tabParam || DEFAULT_ORDER_TAB),
    [tabs, tabParam],
  );

  // A new object identity here would refetch on every render, so the query is
  // memoised on its primitive parts rather than on the tab object.
  const query = useMemo(
    () => ({
      page,
      per_page: String(perPage),
      search_key: search,
      ...activeTab.queryDefaults,
    }),
    [page, perPage, search, activeTab.queryDefaults],
  );

  const {
    orders,
    pagination,
    error,
    isError,
    isLoading,
    isRefetching,
    isEmpty,
    reload,
  } = useOrderList(query);

  // Nothing shows for a fast response; anything shown stays long enough to read.
  const showSkeleton = useDeferredLoading(isLoading);
  const showTabSkeleton = useDeferredLoading(statusOptions.length === 0);

  const isEditable = hasAction(MENU.ORDER, PERMISSION.EDIT);

  /*
   * Hold gating, mirroring the detail screen.
   *
   * Bulk hold is offered only to super admins — for anyone else the backend
   * drops every id into failed_ids, so the action would appear to run and
   * silently change nothing. Rows already on hold are read-only unless the
   * viewer is a super admin.
   */
  const canManageHold = isSuperAdmin;
  const bulkStatusOptions = useMemo(
    () =>
      sortByPath(
        statusOptions.filter(
          (option) =>
            !isRetiredOption(option) &&
            (canManageHold || !isHoldStatus(option.value)),
        ),
      ),
    [statusOptions, canManageHold],
  );

  /* ---------- selection ---------- */

  const visibleIds = useMemo(
    () => orders.map((order) => order.order_id),
    [orders],
  );
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const partiallySelected = selectedIds.size > 0 && !allSelected;

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBulkStatus(null);
  }, []);

  // Selection can't span pages, so anything that changes the visible set drops
  // it rather than leaving invisible rows silently selected.
  useEffect(() => {
    clearSelection();
  }, [page, perPage, search, activeTab.key, clearSelection]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((current) => {
      const next = new Set(current);
      const everyOn = visibleIds.every((id) => next.has(id));
      visibleIds.forEach((id) => (everyOn ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [visibleIds]);

  /* ---------- mutations ---------- */

  const markPending = useCallback((id, pending) => {
    setPendingIds((current) => {
      const next = new Set(current);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const applyRowStatus = useCallback(
    async (order, statusId, { undoable = true } = {}) => {
      const previous = order.status;
      if (previous === statusId) return;

      markPending(order.order_id, true);
      try {
        await axiosInstance.patch('admin/orders/status', {
          id: order.order_id,
          status: statusId,
        });
        dispatch(getAllHeaderCount());
        await reload();
        pushToast(
          `Order ${order.order_number} marked ${statusLabelById.get(statusId) || ''}.`.trim(),
          undoable
            ? {
                undo: () =>
                  applyRowStatus(
                    { ...order, status: statusId },
                    previous,
                    { undoable: false },
                  ),
              }
            : undefined,
        );
      } catch (err) {
        pushToast(readApiError(err, 'Could not update the status.'), {
          tone: 'danger',
        });
      } finally {
        markPending(order.order_id, false);
      }
    },
    [dispatch, markPending, pushToast, reload, statusLabelById],
  );

  /*
   * The tracking prompt's Confirm: save the trip's number (when one was
   * typed), then make the status move it was asked for. A refused number
   * leaves the box open with the refusal in a toast.
   */
  const confirmTrackingPrompt = useCallback(
    async (number) => {
      const prompt = trackingPrompt;
      if (!prompt) return;
      setTrackingPromptPending(true);
      try {
        if (number) {
          try {
            await axiosInstance.post('admin/orders/shipments', {
              id: prompt.order.order_id,
              direction: prompt.direction,
              tracking_number: number,
            });
          } catch (err) {
            pushToast(readApiError(err, 'Could not add the tracking number.'), {
              tone: 'danger',
            });
            return;
          }
        }
        setTrackingPrompt(null);
        await applyRowStatus(prompt.order, prompt.statusId, {
          undoable: !isDestructiveStatus(prompt.statusId),
        });
      } finally {
        setTrackingPromptPending(false);
      }
    },
    [applyRowStatus, pushToast, trackingPrompt],
  );

  const requestRowStatus = useCallback(
    (order, statusId) => {
      // A move that starts a trip asks for the trip's number first, unless
      // the order's newest trip already goes that way.
      const direction = tripDirectionForStatus(statusId);
      if (direction && !tripNumberOnFile(order.shipments, direction)) {
        setTrackingPrompt({ order, statusId, direction });
        return;
      }
      if (!isDestructiveStatus(statusId)) {
        applyRowStatus(order, statusId);
        return;
      }
      if (isReturnStatus(statusId)) {
        setConfirmState({
          title: `Mark order ${order.order_number} as returned?`,
          description:
            'Its items go back into stock. From there it can be sent out again from the status menu, or cancelled — the cancel is what releases the payment and any loyalty points.',
          confirmLabel: 'Mark as returned',
          run: () => applyRowStatus(order, statusId, { undoable: false }),
        });
        return;
      }
      setConfirmState({
        title: `Take order ${order.order_number} off the happy path?`,
        description: `It will be marked ${statusLabelById.get(statusId) || 'an exit status'}. This cannot be undone from here, and any payment must be refunded separately.`,
        confirmLabel: 'Change status',
        run: () => applyRowStatus(order, statusId, { undoable: false }),
      });
    },
    [applyRowStatus, statusLabelById],
  );

  const runBulk = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const result = await dispatch(
      bulkUpdateOrderStatus(ids, bulkStatus, setBulkRunning),
    );
    if (!result?.ok) return;
    const updated = result.updatedIds?.length ?? ids.length;
    const failed = result.failedIds?.length ?? 0;
    pushToast(
      failed > 0
        ? `${updated} updated · ${failed} failed`
        : `${updated} order${updated === 1 ? '' : 's'} updated.`,
      failed > 0 ? { tone: 'danger' } : undefined,
    );
    clearSelection();
    dispatch(getAllHeaderCount());
    reload();
  }, [bulkStatus, clearSelection, dispatch, pushToast, reload, selectedIds]);

  const requestBulk = useCallback(() => {
    if (bulkStatus == null || selectedIds.size === 0) return;
    // A move that starts a trip needs each order's own number, which a bulk
    // action cannot ask for. It runs only when every selected order already
    // has the number on file; otherwise it names how many do not.
    const direction = tripDirectionForStatus(bulkStatus);
    if (direction) {
      const missing = orders.filter(
        (order) => selectedIds.has(order.order_id) && !tripNumberOnFile(order.shipments, direction),
      );
      if (missing.length > 0) {
        const noun = direction === SHIPMENT_DIRECTION.RETURN ? 'return tracking number' : 'tracking number';
        pushToast(
          missing.length === 1
            ? `One of the selected orders has no ${noun} yet. Open it to add the number, or deselect it.`
            : `${missing.length} of the selected orders have no ${noun} yet. Open each one to add the number, or deselect them.`,
          { tone: 'danger' },
        );
        return;
      }
    }
    if (isHoldStatus(bulkStatus)) {
      setConfirmState({
        title: `Put ${selectedIds.size} orders on hold?`,
        description:
          'They stop moving forward, and admins who aren’t super admins won’t be able to change anything on them. Any super admin can resume them.',
        confirmLabel: `Hold ${selectedIds.size} orders`,
        run: runBulk,
      });
      return;
    }
    if (!isDestructiveStatus(bulkStatus)) {
      runBulk();
      return;
    }
    if (isReturnStatus(bulkStatus)) {
      setConfirmState({
        title: `Mark ${selectedIds.size} orders as returned?`,
        description:
          'Unpaid and held orders cannot be returned and will be reported as failed. Items go back into stock; each order can then be sent out again or cancelled.',
        confirmLabel: `Mark ${selectedIds.size} orders as returned`,
        run: runBulk,
      });
      return;
    }
    setConfirmState({
      title: `Change ${selectedIds.size} orders to ${statusLabelById.get(bulkStatus) || 'an exit status'}?`,
      description:
        'This takes every selected order off the happy path and cannot be undone from here. Any payments must be refunded separately.',
      confirmLabel: `Change ${selectedIds.size} orders`,
      run: runBulk,
    });
  }, [bulkStatus, orders, pushToast, runBulk, selectedIds, statusLabelById]);

  const handleRefresh = useCallback(() => {
    reload();
    dispatch(getAllHeaderCount());
  }, [dispatch, reload]);

  /* ---------- render ---------- */

  const columns = useMemo(
    () =>
      buildOrderColumns({
        statusOptions,
        isEditable,
        canManageHold,
        pendingIds,
        onStatusChange: requestRowStatus,
        // The current status leads the list (disabled) so the control has
        // something to display; the rest are legal destinations.
        transitionsFor: (order) =>
          statusPickerOptions({
            fromStatus: order.status,
            statusOptions,
            canHold: canManageHold,
          }),
      }),
    [statusOptions, isEditable, canManageHold, pendingIds, requestRowStatus],
  );

  const cellContext = useMemo(() => ({ statusLabelById }), [statusLabelById]);

  const total = pagination?.total ?? (isEmpty ? 0 : orders.length);
  const rangeStart = orders.length ? (page - 1) * perPage + 1 : 0;
  const rangeEnd = orders.length ? rangeStart + orders.length - 1 : 0;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 bg-nysmah-page-bg">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-nysmah-dark m-0 tracking-tight">
            Orders
          </h1>
          <p className="text-[12.5px] text-nysmah-muted m-0 mt-1">
            Manage and track orders across every status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRefresh}
            aria-label="Refresh orders"
            disabled={isRefetching}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </Button>
          <Button onClick={() => setExportOpen(true)}>
            <Download className="w-4 h-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </header>

      <div className="mb-4">
        <OrderStatusTabs
          tabs={tabs}
          activeKey={activeTab.key}
          headerCount={headerCount}
          totalFromPagination={
            activeTab.kind === 'all' ? pagination?.total : undefined
          }
          isLoading={showTabSkeleton}
          onSelect={(key) => navigate(`/order/list/${key}`)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <SearchBar
          value={search}
          onChange={(value) => patchUrl({ q: value, page: 1 })}
        />
        <span className="text-[12.5px] text-nysmah-muted tabular-nums whitespace-nowrap">
          {showSkeleton ? '—' : `${total} order${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          maxPerBatch={BULK_MAX}
          statusOptions={bulkStatusOptions}
          value={bulkStatus}
          onValueChange={setBulkStatus}
          onApply={requestBulk}
          onClear={clearSelection}
          isRunning={isBulkRunning}
          isEditable={isEditable}
        />
      )}

      <OrdersTable
        orders={orders}
        columns={columns}
        cellContext={cellContext}
        selectedIds={selectedIds}
        onToggleOne={toggleOne}
        onToggleAll={toggleAll}
        allSelected={allSelected}
        partiallySelected={partiallySelected}
        showSkeleton={showSkeleton}
        isRefetching={isRefetching}
        isError={isError}
        error={error}
        isEmpty={isEmpty}
        hasSearch={Boolean(search)}
        onRetry={reload}
        onClearSearch={() => patchUrl({ q: '', page: 1 })}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-[12.5px] text-nysmah-muted">
        <div className="flex items-center gap-3">
          <select
            aria-label="Rows per page"
            value={perPage}
            onChange={(event) =>
              patchUrl({ per_page: event.target.value, page: 1 })
            }
            className="bg-white text-[12.5px] text-[#46555C] px-3 py-1.5 rounded-lg border border-[#DFE6E9] focus:outline-none focus:border-nysmah-accent"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
          {!showSkeleton && orders.length > 0 && (
            <span className="hidden sm:inline tabular-nums">
              Showing {rangeStart}–{rangeEnd} of {total}
            </span>
          )}
        </div>
        {pagination && pagination.pages > 1 && (
          <CommonPagination
            currentPage={pagination.current_page}
            totalPages={pagination.pages}
            onPageChange={(next) => patchUrl({ page: next })}
          />
        )}
      </div>

      {/* Export follows what is on screen — tab, search and all — rather than
        * the tab defaults alone, which silently exported the whole tab. */}
      <ExportDateModal
        isOpen={isExportOpen}
        toggle={() => setExportOpen((open) => !open)}
        params={query}
      />

      <TrackingPromptDialog
        open={trackingPrompt != null}
        onOpenChange={(next) => !next && setTrackingPrompt(null)}
        direction={trackingPrompt?.direction}
        courierLabel={COURIER_LABEL}
        isPending={isTrackingPromptPending}
        onConfirm={confirmTrackingPrompt}
        {...(trackingPrompt
          ? trackingPromptCopy({
              direction: trackingPrompt.direction,
              orderNumber: trackingPrompt.order.order_number || trackingPrompt.order.order_id,
              statusLabel: statusLabelById.get(trackingPrompt.statusId) || '',
              courierLabel: COURIER_LABEL,
            })
          : {})}
      />
      <ConfirmDialog
        open={confirmState != null}
        onOpenChange={(next) => !next && setConfirmState(null)}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        isPending={isBulkRunning}
        onConfirm={async () => {
          const action = confirmState?.run;
          setConfirmState(null);
          await action?.();
        }}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default OrderListPage;
